import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, X, Loader2 } from "lucide-react";
import { apiRaw } from "../lib/api";
import { toast } from "sonner";
import { startGoogleLogin, fetchMe, signupTenantAsUser } from "../lib/auth";

const slugify = (s) =>
    (s || "")
        .toLowerCase()
        .replace(/[^a-z0-9-]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 30);

// Common IANA timezones. Owners in unlisted zones can DM support to add theirs.
const TIMEZONES = [
    "Australia/Sydney",
    "Australia/Melbourne",
    "Australia/Brisbane",
    "Australia/Perth",
    "Australia/Adelaide",
    "Australia/Hobart",
    "Australia/Darwin",
    "Pacific/Auckland",
    "Asia/Singapore",
    "Asia/Hong_Kong",
    "Asia/Tokyo",
    "Asia/Kolkata",
    "Asia/Dubai",
    "Europe/London",
    "Europe/Paris",
    "Europe/Berlin",
    "Europe/Madrid",
    "Europe/Rome",
    "Europe/Amsterdam",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "America/Toronto",
    "America/Vancouver",
    "America/Mexico_City",
    "America/Sao_Paulo",
    "UTC",
];

const CURRENCIES = [
    { code: "AUD", label: "Australian Dollar" },
    { code: "NZD", label: "New Zealand Dollar" },
    { code: "USD", label: "US Dollar" },
    { code: "CAD", label: "Canadian Dollar" },
    { code: "GBP", label: "British Pound" },
    { code: "EUR", label: "Euro" },
    { code: "SGD", label: "Singapore Dollar" },
    { code: "HKD", label: "Hong Kong Dollar" },
    { code: "JPY", label: "Japanese Yen" },
    { code: "INR", label: "Indian Rupee" },
    { code: "AED", label: "UAE Dirham" },
];

const Signup = () => {
    const nav = useNavigate();
    const location = useLocation();
    const [authedUser, setAuthedUser] = useState(location.state?.user || null);
    const [checkedAuth, setCheckedAuth] = useState(!!location.state?.user);
    const [cafeName, setCafeName] = useState("");
    const [slug, setSlug] = useState("");
    const [slugTouched, setSlugTouched] = useState(false);
    const [slugStatus, setSlugStatus] = useState({ checked: false });
    const [ownerName, setOwnerName] = useState("");
    const [ownerEmail, setOwnerEmail] = useState("");
    const [ownerPhone, setOwnerPhone] = useState("");
    const [pin, setPin] = useState("");
    const [timezone, setTimezone] = useState(
        Intl.DateTimeFormat().resolvedOptions().timeZone || "Australia/Sydney"
    );
    const [currency, setCurrency] = useState("AUD");
    const [collectCardNow, setCollectCardNow] = useState(true);
    const [initialTier, setInitialTier] = useState("starter");
    const [submitting, setSubmitting] = useState(false);

    // Check for existing Google session (in case user hit /signup with a valid cookie)
    useEffect(() => {
        if (checkedAuth) return;
        fetchMe().then((u) => {
            if (u) {
                setAuthedUser(u);
                setOwnerName(u.name || "");
                setOwnerEmail(u.email || "");
            }
            setCheckedAuth(true);
        });
    }, [checkedAuth]);

    // If authed, pre-fill name/email from Google
    useEffect(() => {
        if (authedUser) {
            if (authedUser.name && !ownerName) setOwnerName(authedUser.name);
            if (authedUser.email && !ownerEmail) setOwnerEmail(authedUser.email);
        }
    }, [authedUser]); // eslint-disable-line

    // Auto-derive slug from café name if not manually edited
    useEffect(() => {
        if (!slugTouched) setSlug(slugify(cafeName));
    }, [cafeName, slugTouched]);

    // Debounced slug availability check
    useEffect(() => {
        if (!slug || slug.length < 3) {
            setSlugStatus({ checked: false });
            return;
        }
        setSlugStatus({ checking: true });
        const t = setTimeout(() => {
            apiRaw
                .get(`/signup/check-slug/${slug}`)
                .then((r) => setSlugStatus({ checked: true, ...r.data }))
                .catch(() => setSlugStatus({ checked: true, available: false, reason: "Check failed" }));
        }, 350);
        return () => clearTimeout(t);
    }, [slug]);

    const googleAuthed = !!authedUser;

    // In Google-authed mode we don't need email; in PIN mode we do.
    const canSubmit =
        cafeName.trim().length >= 1 &&
        slug.length >= 3 &&
        slugStatus.available === true &&
        (googleAuthed || (ownerName.trim().length >= 1 && /^\S+@\S+\.\S+$/.test(ownerEmail))) &&
        ownerPhone.replace(/\D/g, "").length >= 6 &&
        (googleAuthed || /^\d{4,8}$/.test(pin)) &&
        !submitting;

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            if (googleAuthed) {
                // Google-authed path — session cookie carries identity
                const r = await signupTenantAsUser({
                    slug,
                    name: cafeName.trim(),
                    contact_phone: ownerPhone.trim(),
                    admin_pin: pin || undefined,
                    currency,
                    timezone,
                    collect_card_now: collectCardNow,
                    initial_tier: initialTier,
                    origin_url: window.location.origin,
                });
                // If the owner chose to attach a card upfront, redirect to
                // the Stripe checkout URL that the backend prepared for us.
                if (collectCardNow && r?.checkout_url) {
                    window.location.assign(r.checkout_url);
                    return;
                }
                nav(`/signup/success/${slug}`, {
                    state: { pin: r.admin_pin || pin, cafeName: cafeName.trim(), googleAuthed: true },
                });
            } else {
                // Legacy PIN-only signup
                await apiRaw.post("/signup", {
                    slug,
                    name: cafeName.trim(),
                    owner_name: ownerName.trim(),
                    owner_email: ownerEmail.trim(),
                    owner_phone: ownerPhone.trim(),
                    admin_pin: pin,
                    currency,
                    timezone,
                });
                nav(`/signup/success/${slug}`, { state: { pin, cafeName: cafeName.trim() } });
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sign up failed. Try again.");
            setSubmitting(false);
        }
    };

    const doGoogle = () => startGoogleLogin("/signup");

    return (
        <div className="min-h-screen bg-[#F9F6F0] text-[#2C1A12]">
            <header className="border-b border-[#E8DCCB]/60">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <button
                        data-testid="signup-back-btn"
                        onClick={() => nav("/")}
                        className="flex items-center gap-2 text-sm font-semibold text-[#8C7A72] hover:text-[#2C1A12]"
                    >
                        <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> Back
                    </button>
                    <div className="font-display font-black text-xl">
                        Coffee<span className="text-[#FF4F18]">in7.</span>
                    </div>
                    <div className="w-16" />
                </div>
            </header>

            <div className="max-w-2xl mx-auto px-6 py-12">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                    <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                        Start your café
                    </div>
                    <h1 className="mt-3 font-display font-black text-4xl sm:text-5xl leading-tight">
                        Live in 10 minutes.
                    </h1>
                    <p className="mt-3 text-[#8C7A72] max-w-lg">
                        Free during our beta. No card required. Once signed up, you'll have 3
                        working links: customer ordering, kitchen queue, and your owner dashboard.
                    </p>
                </motion.div>

                {/* Google auth CTA at the top — huge upgrade for owners */}
                {checkedAuth && !googleAuthed && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        className="mt-8 rounded-3xl bg-[#2C1A12] text-white p-5"
                    >
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex-1">
                                <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                                    Fastest path
                                </div>
                                <div className="mt-1 font-display font-bold text-lg leading-snug">
                                    Continue with Google
                                </div>
                                <div className="text-white/70 text-xs mt-1">
                                    We'll fill in your name and email · sign in later with one tap
                                </div>
                            </div>
                            <button
                                data-testid="signup-google-btn"
                                onClick={doGoogle}
                                className="shrink-0 rounded-full bg-white text-[#2C1A12] px-5 py-3 text-sm font-display font-bold flex items-center gap-2 active:scale-95"
                            >
                                <GoogleColorIcon />
                                Google
                            </button>
                        </div>
                    </motion.div>
                )}
                {googleAuthed && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mt-8 rounded-2xl bg-[#1B995E]/10 border border-[#1B995E]/30 p-4 flex items-center gap-3"
                        data-testid="signup-authed-badge"
                    >
                        {authedUser.picture && (
                            <img src={authedUser.picture} alt="" className="h-9 w-9 rounded-full" />
                        )}
                        <div className="flex-1">
                            <div className="text-sm font-display font-bold text-[#1B995E]">
                                Signed in as {authedUser.name || authedUser.email}
                            </div>
                            <div className="text-xs text-[#8C7A72]">
                                {authedUser.email}
                            </div>
                        </div>
                    </motion.div>
                )}

                <div className="mt-10 space-y-6">
                    <Field
                        label="Café name"
                        hint="Shows on your customers' phone screens"
                        testid="signup-cafe-name"
                        value={cafeName}
                        onChange={setCafeName}
                        placeholder="e.g. Blue Bottle"
                    />

                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">
                            Your café's URL
                        </div>
                        <div className="mt-2 rounded-2xl bg-white border-2 border-[#E8DCCB] overflow-hidden flex focus-within:border-[#FF4F18] transition-colors">
                            <div className="px-4 py-3 bg-[#F9F6F0] border-r border-[#E8DCCB] text-[#8C7A72] text-sm font-mono">
                                coffeein7.com/
                            </div>
                            <input
                                data-testid="signup-slug"
                                type="text"
                                value={slug}
                                onChange={(e) => {
                                    setSlug(slugify(e.target.value));
                                    setSlugTouched(true);
                                }}
                                placeholder="yourcafe"
                                className="flex-1 px-3 py-3 bg-white outline-none font-mono text-sm"
                            />
                            <div className="px-4 py-3 flex items-center min-w-[100px] justify-center" data-testid="signup-slug-status">
                                {slugStatus.checking && (
                                    <Loader2 className="h-4 w-4 text-[#8C7A72] animate-spin" strokeWidth={2.5} />
                                )}
                                {slugStatus.checked && slugStatus.available && (
                                    <span className="flex items-center gap-1 text-[#1B995E] text-xs font-bold">
                                        <Check className="h-3.5 w-3.5" strokeWidth={3} /> Free
                                    </span>
                                )}
                                {slugStatus.checked && slugStatus.available === false && (
                                    <span className="flex items-center gap-1 text-[#FF4F18] text-xs font-bold">
                                        <X className="h-3.5 w-3.5" strokeWidth={3} /> Taken
                                    </span>
                                )}
                            </div>
                        </div>
                        {slugStatus.checked && slugStatus.reason && !slugStatus.available && (
                            <p className="mt-1 text-xs text-[#FF4F18]">{slugStatus.reason}</p>
                        )}
                        <p className="mt-1 text-xs text-[#8C7A72]">
                            This is the link your customers will scan. Lowercase, a-z, 0-9, hyphens.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        {!googleAuthed && (
                            <Field
                                label="Owner name"
                                testid="signup-owner-name"
                                value={ownerName}
                                onChange={setOwnerName}
                                placeholder="e.g. Alex"
                            />
                        )}
                        <Field
                            label="Owner phone"
                            testid="signup-owner-phone"
                            value={ownerPhone}
                            onChange={setOwnerPhone}
                            placeholder="0400 000 000"
                            type="tel"
                        />
                    </div>

                    {!googleAuthed && (
                        <Field
                            label="Owner email"
                            hint="We'll send your dashboard links here"
                            testid="signup-owner-email"
                            value={ownerEmail}
                            onChange={setOwnerEmail}
                            placeholder="you@yourcafe.com.au"
                            type="email"
                        />
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">
                                Timezone
                            </div>
                            <select
                                data-testid="signup-timezone-select"
                                value={timezone}
                                onChange={(e) => setTimezone(e.target.value)}
                                className="mt-2 w-full px-4 py-3 bg-white border-2 border-[#E8DCCB] rounded-2xl outline-none focus:border-[#FF4F18] text-base"
                            >
                                {TIMEZONES.map((tz) => (
                                    <option key={tz} value={tz}>{tz}</option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-[#8C7A72]">
                                Auto-detected from your browser. Pickup times &amp; open hours use this.
                            </p>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">
                                Currency
                            </div>
                            <select
                                data-testid="signup-currency-select"
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                className="mt-2 w-full px-4 py-3 bg-white border-2 border-[#E8DCCB] rounded-2xl outline-none focus:border-[#FF4F18] text-base"
                            >
                                {CURRENCIES.map((c) => (
                                    <option key={c.code} value={c.code}>{c.code} · {c.label}</option>
                                ))}
                            </select>
                            <p className="mt-1 text-xs text-[#8C7A72]">
                                Shown on menu prices and receipts.
                            </p>
                        </div>
                    </div>

                    <div>
                        <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">
                            Admin PIN {googleAuthed && <span className="normal-case tracking-normal text-[#E8DCCB]">— optional (staff bypass)</span>}
                        </div>
                        <input
                            data-testid="signup-admin-pin"
                            type="password"
                            inputMode="numeric"
                            maxLength={8}
                            value={pin}
                            onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                            placeholder={googleAuthed ? "Leave blank for auto-generated PIN" : "4-8 digits"}
                            className="mt-2 w-full px-4 py-3 bg-white border-2 border-[#E8DCCB] rounded-2xl outline-none focus:border-[#FF4F18] font-mono text-lg tracking-widest"
                        />
                        <p className="mt-1 text-xs text-[#8C7A72]">
                            {googleAuthed
                                ? "Baristas can use this PIN to log in without your Google account."
                                : "You'll use this to log into your owner dashboard. Change it anytime."}
                        </p>
                    </div>

                    {googleAuthed && (
                        <div className="rounded-2xl border-2 border-[#E8DCCB] p-4 bg-[#FDFAF4]">
                            <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">
                                Payment method
                            </div>
                            <div className="mt-3 space-y-2">
                                <label
                                    className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${
                                        collectCardNow ? "border-[#FF4F18] bg-white" : "border-[#E8DCCB]"
                                    }`}
                                    data-testid="signup-card-now-option"
                                >
                                    <input
                                        type="radio"
                                        checked={collectCardNow}
                                        onChange={() => setCollectCardNow(true)}
                                        className="mt-1 accent-[#FF4F18]"
                                    />
                                    <div>
                                        <div className="font-display font-bold text-[#2C1A12]">
                                            Add card now · Recommended
                                        </div>
                                        <div className="text-xs text-[#6B5A52] mt-0.5">
                                            $0 for 90 days. Card auto-charges only on day 91. Cancel anytime — no lock-in.
                                        </div>
                                    </div>
                                </label>
                                <label
                                    className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${
                                        !collectCardNow ? "border-[#FF4F18] bg-white" : "border-[#E8DCCB]"
                                    }`}
                                    data-testid="signup-card-later-option"
                                >
                                    <input
                                        type="radio"
                                        checked={!collectCardNow}
                                        onChange={() => setCollectCardNow(false)}
                                        className="mt-1 accent-[#FF4F18]"
                                    />
                                    <div>
                                        <div className="font-display font-bold text-[#2C1A12]">
                                            Skip for now
                                        </div>
                                        <div className="text-xs text-[#6B5A52] mt-0.5">
                                            Get your first orders flowing. Add a card before day 90 or your café pauses for 7 days then goes offline.
                                        </div>
                                    </div>
                                </label>
                            </div>
                            {collectCardNow && (
                                <div className="mt-4 flex gap-2" data-testid="signup-tier-picker">
                                    <button
                                        type="button"
                                        onClick={() => setInitialTier("starter")}
                                        className={`flex-1 py-2.5 rounded-full text-sm font-display font-bold border-2 transition ${
                                            initialTier === "starter"
                                                ? "bg-[#2C1A12] text-white border-[#2C1A12]"
                                                : "bg-white text-[#2C1A12] border-[#E8DCCB]"
                                        }`}
                                    >
                                        Starter · $49/mo
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setInitialTier("pro")}
                                        className={`flex-1 py-2.5 rounded-full text-sm font-display font-bold border-2 transition ${
                                            initialTier === "pro"
                                                ? "bg-[#2C1A12] text-white border-[#2C1A12]"
                                                : "bg-white text-[#2C1A12] border-[#E8DCCB]"
                                        }`}
                                    >
                                        Pro · $79/mo
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <button
                    data-testid="signup-submit-btn"
                    disabled={!canSubmit}
                    onClick={submit}
                    className={`mt-10 w-full py-5 rounded-full font-display font-bold text-lg flex items-center justify-center gap-2 transition-all ${
                        canSubmit
                            ? "bg-[#FF4F18] text-white shadow-[0_16px_40px_rgba(255,79,24,0.32)] active:scale-[0.98]"
                            : "bg-[#E8DCCB] text-[#8C7A72] cursor-not-allowed"
                    }`}
                >
                    {submitting
                        ? (collectCardNow && googleAuthed ? "Redirecting to Stripe…" : "Creating your café…")
                        : (collectCardNow && googleAuthed ? "Create café + add card →" : "Create my café")}
                    {!submitting && <ArrowRight className="h-5 w-5" strokeWidth={2.5} />}
                </button>

                <p className="mt-6 text-center text-xs text-[#8C7A72]">
                    By creating a café you agree to the (imaginary) terms. During beta, everything is free.
                </p>

                <div
                    className="mt-6 rounded-2xl bg-white border border-[#E8DCCB] p-4 text-center"
                    data-testid="signup-help-contact"
                >
                    <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                        Need a hand?
                    </div>
                    <p className="mt-2 text-sm text-[#8C7A72]">
                        Prefer a walkthrough or have questions before signing up?
                    </p>
                    <div className="mt-2 flex flex-col items-center gap-0.5">
                        <a
                            href="mailto:admin@coffeein7.com?subject=Café%20onboarding%20enquiry"
                            className="text-sm font-display font-bold text-[#2C1A12] hover:text-[#FF4F18]"
                        >
                            admin@coffeein7.com
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Field = ({ label, hint, testid, value, onChange, placeholder, type = "text" }) => (
    <div>
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#8C7A72]">{label}</div>
        <input
            data-testid={testid}
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="mt-2 w-full px-4 py-3 bg-white border-2 border-[#E8DCCB] rounded-2xl outline-none focus:border-[#FF4F18] text-base"
        />
        {hint && <p className="mt-1 text-xs text-[#8C7A72]">{hint}</p>}
    </div>
);

const GoogleColorIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
        <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.4 14.6 2.4 12 2.4 6.8 2.4 2.6 6.6 2.6 12S6.8 21.6 12 21.6c6.9 0 9.5-4.8 9.5-7.3 0-.5 0-.9-.1-1.3L12 10.2z"/>
    </svg>
);

export default Signup;
